import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Shield, Clock, AlertTriangle, Calculator, FileText,
  Calendar, CheckCircle2, XCircle, MinusCircle, TrendingUp,
  BarChart3, AlertOctagon, Package, Zap, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatMoney = (val) => {
  if (!val && val !== 0) return '-';
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(val);
};

const DURUM_ICON = {
  uygun: { icon: CheckCircle2, color: '#22c55e' },
  uygunsuz: { icon: XCircle, color: '#ef4444' },
  kontrol_edilmedi: { icon: MinusCircle, color: '#6b7280' },
};

const CIDDIYET_COLOR = { kritik: '#ef4444', yuksek: '#f59e0b', orta: '#3b82f6', dusuk: '#6b7280' };

export default function ISYKontrolPaneli({ ihaleler, onNavigate }) {
  const [selectedIhale, setSelectedIhale] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [proaktifLoading, setProaktifLoading] = useState(false);
  const [proaktifResult, setProaktifResult] = useState(null);

  const fetchKontrol = useCallback(async (ihaleId) => {
    setLoading(true);
    setProaktifResult(null);
    try {
      const res = await axios.get(`${API}/isy/kontrol-paneli/${ihaleId}`, { withCredentials: true });
      setData(res.data);
    } catch (e) { toast.error('Veri yüklenemedi'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selectedIhale) fetchKontrol(selectedIhale.id); }, [selectedIhale, fetchKontrol]);

  const handleProaktif = async () => {
    if (!selectedIhale) return;
    setProaktifLoading(true);
    try {
      const res = await axios.post(`${API}/isy/asistan/proaktif-analiz/${selectedIhale.id}`, {}, { withCredentials: true });
      setProaktifResult(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Analiz hatası'); }
    finally { setProaktifLoading(false); }
  };

  if (!selectedIhale) {
    return (
      <div className="space-y-4" data-testid="isy-kontrol-paneli">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-cyan-400" />
          <h2 className="text-lg font-bold text-white">Kontrol Paneli</h2>
        </div>
        <p className="text-sm text-gray-400">Detaylı incelemek istediğiniz ihaleyi seçin:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(ihaleler || []).map(ih => (
            <Card key={ih.id} className="bg-gray-900/60 border-gray-700/50 hover:border-cyan-500/40 cursor-pointer transition-all hover:scale-[1.01]"
              onClick={() => setSelectedIhale(ih)} data-testid={`kontrol-select-${ih.id}`}>
              <CardContent className="py-3 px-4">
                <p className="text-sm font-semibold text-white">{ih.is_adi}</p>
                <p className="text-xs text-gray-500 mt-1">IKN: {ih.ikn || '-'} | Aşama: {ih.mevcut_asama}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="isy-kontrol-paneli">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const { ihale, ozet } = data;

  const getSkorStyle = (skor) => {
    if (skor >= 80) return { color: '#22c55e', label: 'İYİ' };
    if (skor >= 60) return { color: '#f59e0b', label: 'DİKKAT' };
    if (skor >= 40) return { color: '#f97316', label: 'RİSKLİ' };
    return { color: '#ef4444', label: 'KRİTİK' };
  };

  const skorStyle = getSkorStyle(ozet.saglik_skoru);
  const skorColor = skorStyle.color;
  const durumLabel = skorStyle.label;

  return (
    <div className="space-y-4" data-testid="isy-kontrol-paneli">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setSelectedIhale(null); setData(null); }} className="text-gray-400 hover:text-white h-8 px-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> Geri
          </Button>
          <div>
            <h2 className="text-sm font-bold text-white">{ihale.is_adi}</h2>
            <p className="text-xs text-gray-500">IKN: {ihale.ikn || '-'} | {ihale.sozlesme_turu} | {ihale.mevcut_asama}</p>
          </div>
        </div>
        <Button onClick={handleProaktif} disabled={proaktifLoading} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="kontrol-proaktif">
          {proaktifLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
          AI Proaktif Analiz
        </Button>
      </div>

      {/* Sağlık Skoru Büyük Kart */}
      <div className="grid grid-cols-12 gap-3">
        <Card className="col-span-12 lg:col-span-4 bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-5 pb-4 text-center">
            <div className="w-24 h-24 rounded-full mx-auto mb-3 flex items-center justify-center border-4" style={{ borderColor: skorColor }}>
              <div>
                <p className="text-3xl font-bold" style={{ color: skorColor }}>{ozet.saglik_skoru}</p>
                <p className="text-[9px] text-gray-500">/ 100</p>
              </div>
            </div>
            <Badge style={{ background: skorColor + '22', color: skorColor, border: `1px solid ${skorColor}44` }} className="text-xs px-3 py-1">{durumLabel}</Badge>
            <div className="grid grid-cols-2 gap-2 mt-4">
              {[
                { label: 'Uyum', val: ozet.puan_uyum, icon: Shield, color: '#3b82f6' },
                { label: 'Gecikme', val: ozet.puan_gecikme, icon: Clock, color: '#f59e0b' },
                { label: 'Eksiklik', val: ozet.puan_eksiklik, icon: AlertTriangle, color: '#ef4444' },
                { label: 'İş Artışı', val: ozet.puan_is_artisi, icon: TrendingUp, color: '#8b5cf6' },
              ].map(p => (
                <div key={p.label} className="text-center">
                  <p.icon className="w-3 h-3 mx-auto mb-0.5" style={{ color: p.color }} />
                  <p className="text-xs font-semibold text-white">{p.val}</p>
                  <p className="text-[9px] text-gray-500">{p.label}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* İhale Bilgileri */}
        <Card className="col-span-12 lg:col-span-8 bg-gray-900/80 border-gray-700/50">
          <CardHeader className="pb-2"><CardTitle className="text-xs text-gray-400">İhale Bilgileri</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: 'Yaklaşık Maliyet', val: formatMoney(ihale.yaklasik_maliyet), color: 'text-blue-400' },
                { label: 'Sözleşme Bedeli', val: formatMoney(ihale.sozlesme_bedeli), color: 'text-green-400' },
                { label: 'İş Artışı', val: `%${ihale.is_artisi_orani || 0}`, color: ihale.is_artisi_orani > 10 ? 'text-red-400' : 'text-gray-300' },
                { label: 'Sözleşme Süresi', val: `${ihale.sozlesme_suresi_gun || '-'} gün`, color: 'text-gray-300' },
                { label: 'Keşif Bedeli', val: formatMoney(ozet.kesif_toplam_bedel), color: 'text-amber-400' },
                { label: 'Gecikme', val: ozet.gecikme_gun > 0 ? `${ozet.gecikme_gun} gün` : 'Yok', color: ozet.gecikme_gun > 0 ? 'text-red-400' : 'text-green-400' },
                { label: 'Süre Uzatma', val: ozet.sure_onay_uzatma > 0 ? `${ozet.sure_onay_uzatma} gün` : 'Yok', color: 'text-amber-400' },
                { label: 'Belge', val: `${ozet.belge_sayisi} adet`, color: 'text-gray-300' },
              ].map(item => (
                <div key={item.label} className="p-2 bg-gray-800/40 rounded">
                  <p className="text-[10px] text-gray-500">{item.label}</p>
                  <p className={`text-sm font-semibold ${item.color}`}>{item.val}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alt Bölümler - Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Şartname Uyum */}
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardHeader className="pb-1">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs text-gray-400 flex items-center gap-1"><Shield className="w-3.5 h-3.5 text-blue-400" /> Şartname Uyum</CardTitle>
              <span className="text-xs font-semibold" style={{ color: getSkorStyle(ozet.sartname_skor).color }}>
                %{ozet.sartname_skor}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 mb-2 text-[10px]">
              <span className="text-green-400">{ozet.sartname_uygun} Uygun</span>
              <span className="text-red-400">{ozet.sartname_uygunsuz} Uygunsuz</span>
              <span className="text-gray-500">{ozet.sartname_toplam - ozet.sartname_uygun - ozet.sartname_uygunsuz} Kontrol Edilmedi</span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-1.5 mb-2">
              <div className="h-1.5 rounded-full" style={{ width: `${ozet.sartname_skor}%`, background: ozet.sartname_skor >= 70 ? '#22c55e' : '#f59e0b' }} />
            </div>
            {(data.sartname || []).slice(0, 5).map(s => {
              const d = DURUM_ICON[s.durum] || DURUM_ICON.kontrol_edilmedi;
              const Icon = d.icon;
              return (
                <div key={s.id} className="flex items-center gap-2 py-0.5">
                  <Icon className="w-3 h-3 flex-shrink-0" style={{ color: d.color }} />
                  <span className="text-[10px] text-gray-400 w-10">Md.{s.madde_no}</span>
                  <span className="text-[10px] text-gray-300 truncate">{s.madde_adi}</span>
                </div>
              );
            })}
            {ozet.sartname_toplam > 5 && <p className="text-[9px] text-gray-600 mt-1">+{ozet.sartname_toplam - 5} madde daha</p>}
          </CardContent>
        </Card>

        {/* Eksiklikler */}
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardHeader className="pb-1">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs text-gray-400 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-red-400" /> Eksiklikler</CardTitle>
              <div className="flex items-center gap-2 text-[10px]">
                <span className="text-red-400">{ozet.eksiklik_acik} Açık</span>
                {ozet.eksiklik_kritik > 0 && <span className="text-red-500 font-bold">{ozet.eksiklik_kritik} Kritik</span>}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {(data.eksiklikler || []).length === 0 ? (
              <p className="text-[10px] text-gray-600 py-2">Eksiklik kaydı yok</p>
            ) : (
              (data.eksiklikler || []).slice(0, 5).map(e => (
                <div key={e.id} className="flex items-center gap-2 py-1 border-b border-gray-800/30 last:border-0">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: CIDDIYET_COLOR[e.ciddiyet] || '#6b7280' }} />
                  <span className="text-[10px] text-gray-300 flex-1 truncate">{e.baslik}</span>
                  <Badge className="text-[8px] px-1 py-0" style={{ background: (CIDDIYET_COLOR[e.ciddiyet] || '#6b7280') + '22', color: CIDDIYET_COLOR[e.ciddiyet] || '#6b7280' }}>
                    {e.ciddiyet}
                  </Badge>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Keşif Özet */}
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardHeader className="pb-1">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs text-gray-400 flex items-center gap-1"><Calculator className="w-3.5 h-3.5 text-amber-400" /> Keşif</CardTitle>
              <span className="text-xs font-semibold text-amber-400">{formatMoney(ozet.kesif_toplam_bedel)}</span>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-[10px] text-gray-500 mb-1">{ozet.kesif_kalem_sayisi} kalem</p>
            {(data.kesif || []).slice(0, 5).map(k => (
              <div key={k.id} className="flex items-center justify-between py-0.5">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] text-gray-500 w-14 flex-shrink-0">{k.poz_no || '-'}</span>
                  <span className="text-[10px] text-gray-300 truncate">{k.is_kalemi_adi}</span>
                </div>
                <span className="text-[10px] text-amber-400 flex-shrink-0 ml-2">{formatMoney(k.tutar)}</span>
              </div>
            ))}
            {ozet.kesif_kalem_sayisi > 5 && <p className="text-[9px] text-gray-600 mt-1">+{ozet.kesif_kalem_sayisi - 5} kalem daha</p>}
          </CardContent>
        </Card>

        {/* Süre Olayları */}
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardHeader className="pb-1">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs text-gray-400 flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-blue-400" /> Süre Olayları</CardTitle>
              <div className="text-[10px]">
                {ozet.sure_onay_uzatma > 0 && <span className="text-amber-400">{ozet.sure_onay_uzatma}g uzatma</span>}
                {ozet.gecikme_gun > 0 && <span className="text-red-400 ml-2">{ozet.gecikme_gun}g gecikme</span>}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {(data.sure_olaylari || []).length === 0 ? (
              <p className="text-[10px] text-gray-600 py-2">Süre olayı yok</p>
            ) : (
              (data.sure_olaylari || []).slice(0, 5).map(s => (
                <div key={s.id} className="flex items-center gap-2 py-0.5">
                  <span className="text-[10px] text-gray-500 w-20 flex-shrink-0">{s.tarih}</span>
                  <span className="text-[10px] text-gray-300 flex-1 truncate">{s.baslik}</span>
                  {s.gun_sayisi && <span className="text-[10px] text-amber-400">{s.gun_sayisi}g</span>}
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Belgeler & Takvim */}
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardHeader className="pb-1">
            <CardTitle className="text-xs text-gray-400 flex items-center gap-1"><FileText className="w-3.5 h-3.5 text-indigo-400" /> Son Belgeler ({ozet.belge_sayisi})</CardTitle>
          </CardHeader>
          <CardContent>
            {(data.belgeler || []).length === 0 ? (
              <p className="text-[10px] text-gray-600 py-2">Belge yok</p>
            ) : (
              (data.belgeler || []).slice(0, 4).map(b => (
                <div key={b.id} className="flex items-center gap-2 py-0.5">
                  <FileText className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                  <span className="text-[10px] text-gray-300 truncate">{b.baslik}</span>
                  <span className="text-[9px] text-gray-600 flex-shrink-0">{b.created_at?.split('T')[0]}</span>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardHeader className="pb-1">
            <CardTitle className="text-xs text-gray-400 flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-teal-400" /> Yaklaşan Olaylar ({ozet.takvim_olay_sayisi})</CardTitle>
          </CardHeader>
          <CardContent>
            {(data.takvim || []).length === 0 ? (
              <p className="text-[10px] text-gray-600 py-2">Takvim olayı yok</p>
            ) : (
              (data.takvim || []).slice(0, 4).map(t => (
                <div key={t.id} className="flex items-center gap-2 py-0.5">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: t.renk || '#6b7280' }} />
                  <span className="text-[10px] text-gray-500 w-20 flex-shrink-0">{t.tarih}</span>
                  <span className={`text-[10px] flex-1 truncate ${t.tamamlandi ? 'text-gray-600 line-through' : 'text-gray-300'}`}>{t.baslik}</span>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Proaktif Analiz Sonucu */}
      {proaktifLoading && (
        <Card className="bg-amber-900/10 border-amber-800/20">
          <CardContent className="py-4 flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
            <span className="text-sm text-amber-300">AI proaktif analiz yapılıyor...</span>
          </CardContent>
        </Card>
      )}
      {proaktifResult && !proaktifLoading && (
        <Card className="bg-gray-900/80 border-amber-800/20">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs text-amber-400 flex items-center gap-1"><Zap className="w-4 h-4" /> AI Proaktif Analiz Raporu</CardTitle>
              <button onClick={() => setProaktifResult(null)} className="text-gray-500 hover:text-gray-300 text-xs">Kapat</button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-gray-200 whitespace-pre-wrap leading-relaxed max-h-80 overflow-y-auto" data-testid="kontrol-proaktif-sonuc">
              {proaktifResult.analiz}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
